import { hasText } from 'shared/lib/common-util'
import { NctsCountry, supportsPreLodgedDeclaration } from 'config'

export type ActionRuleContext = {
  lrn: string | null
  mrn: string | null
  status: string | null
  country: string
  role: string
  security: string
  hasSubmissionConfirmedMessage: boolean
  hasMRNAllocatedMessage: boolean
  hasReleaseWithoutMrnMessage: boolean
  hasEnsPresentationNotificationAcceptedMessage: boolean
  isEnsPresentationNotificationSubmissionPending: boolean
  isPreLodgedPresentationNotificationPending: boolean
  isPreLodgedPresentationNotificationCorrectionPending: boolean
  additionalDeclarationType: string
}

export const isPreLodgedDeclaration = ({
  country,
  additionalDeclarationType,
}: Pick<ActionRuleContext, 'country' | 'additionalDeclarationType'>): boolean => additionalDeclarationType === 'D'
  && supportsPreLodgedDeclaration(country as NctsCountry)

export type PreLodgedDeclarationConfig = {
  requiresRegistration: boolean
  cancelAfterControlNotice: boolean
  extraClosingSuffixes?: string[]
  extraPresentationCompletedSuffixes?: string[]
}

export const PRE_LODGED_DECLARATION_CONFIGS = {
  SK: {
    requiresRegistration: true,
    cancelAfterControlNotice: false,
  },
  RS: {
    requiresRegistration: true,
    cancelAfterControlNotice: true,
    extraClosingSuffixes: ['IEA28', 'IE029', 'IEA29', 'IEA91'],
    extraPresentationCompletedSuffixes: ['IEA28', 'IE029'],
  },
} satisfies Partial<Record<NctsCountry, PreLodgedDeclarationConfig>>

export const getPreLodgedDeclarationConfig = (country: string): PreLodgedDeclarationConfig | undefined => PRE_LODGED_DECLARATION_CONFIGS[
  country as keyof typeof PRE_LODGED_DECLARATION_CONFIGS
]

const PRE_LODGED_PRESENTATION_NOTIFICATION_STATUSES = [
  'CONTROL_DECISION',
  'AMENDMENT_ACCEPTANCE',
  'SUBMISSION_CONFIRMED',
  'REJECTED',
  'INVALID_CANCELLATION',
]

export const isPreLodgedPresentationNotificationAvailable = ({
  status,
  country,
  additionalDeclarationType,
  hasSubmissionConfirmedMessage,
  hasMRNAllocatedMessage,
  hasReleaseWithoutMrnMessage,
  isPreLodgedPresentationNotificationPending,
  isPreLodgedPresentationNotificationCorrectionPending,
}: ActionRuleContext): boolean => {
  if (!isPreLodgedDeclaration({ country, additionalDeclarationType })) {
    return false
  }

  if (!PRE_LODGED_PRESENTATION_NOTIFICATION_STATUSES.includes(status ?? '')) {
    return false
  }

  if (getPreLodgedDeclarationConfig(country)?.requiresRegistration && !hasSubmissionConfirmedMessage) {
    return false
  }

  // A refused presentation is corrected by amending, not by presenting again:
  // offering IE170 here produces the out-of-sequence error that refusing it did
  // not make obvious.
  if (isPreLodgedPresentationNotificationCorrectionPending) {
    return false
  }

  return !hasMRNAllocatedMessage && !hasReleaseWithoutMrnMessage && !isPreLodgedPresentationNotificationPending
}

const isPreLodgedPresentationOutstanding = ({
  country,
  additionalDeclarationType,
  isPreLodgedPresentationNotificationPending,
}: Pick<
ActionRuleContext,
'country' | 'additionalDeclarationType' | 'isPreLodgedPresentationNotificationPending'
>): boolean => isPreLodgedDeclaration({ country, additionalDeclarationType })
  && isPreLodgedPresentationNotificationPending

export type EnsPresentationNotificationConfig = {
  requiresMrn: boolean
  acceptedSuffix?: string
  pending?: {
    submissionSuffix: string
    closingSuffixes: string[]
    strategy: 'ordered' | 'count'
  }
}

export const ENS_PRESENTATION_NOTIFICATION_CONFIGS = {
  LT: { requiresMrn: false },
  RO: {
    requiresMrn: true,
    acceptedSuffix: 'IETS028',
    pending: {
      submissionSuffix: 'IETS007',
      closingSuffixes: ['IETS928', 'IETS016'],
      strategy: 'count',
    },
  },
  PL: {
    requiresMrn: true,
    pending: {
      submissionSuffix: 'ICS100',
      closingSuffixes: ['ICS002', 'ICS003', 'ICS005', 'ICS006'],
      strategy: 'ordered',
    },
  },
} satisfies Partial<Record<NctsCountry, EnsPresentationNotificationConfig>>

export type EnsPresentationNotificationCountry = keyof typeof ENS_PRESENTATION_NOTIFICATION_CONFIGS

export const getEnsPresentationNotificationConfig = (
  country: string,
): EnsPresentationNotificationConfig | undefined => ENS_PRESENTATION_NOTIFICATION_CONFIGS[
  country as EnsPresentationNotificationCountry
]

export const isEnsPresentationNotificationAvailable = ({
  lrn,
  mrn,
  country,
  hasEnsPresentationNotificationAcceptedMessage,
  isEnsPresentationNotificationSubmissionPending,
}: ActionRuleContext): boolean => {
  const config = getEnsPresentationNotificationConfig(country)

  if (!config) {
    return false
  }

  if (config.requiresMrn ? !hasText(mrn) : !hasText(lrn) && !hasText(mrn)) {
    return false
  }

  if (config.acceptedSuffix && hasEnsPresentationNotificationAcceptedMessage) {
    return false
  }

  return !(config.pending && isEnsPresentationNotificationSubmissionPending)
}

const hasEmtaEarlyMrnFromAck = ({
  country,
  security,
  mrn,
  hasSubmissionConfirmedMessage,
  hasMRNAllocatedMessage,
}: Pick<ActionRuleContext, 'country' | 'security' | 'mrn' | 'hasSubmissionConfirmedMessage' | 'hasMRNAllocatedMessage'>): boolean => country === 'EE'
  && (security === '1' || security === '3')
  && hasSubmissionConfirmedMessage
  && (hasText(mrn) || hasMRNAllocatedMessage)

export function isSubmitDisabled({
  status,
  mrn,
  country,
  security,
  hasSubmissionConfirmedMessage,
  hasMRNAllocatedMessage,
  role,
}: ActionRuleContext) {
  if (status === null || ['SUBMITTED', 'DELETED', 'CANCEL_REQUESTED'].includes(status)) {
    return true
  }

  if (status === 'DRAFT') {
    return false
  }
  if (status === 'REJECTED') {
    return hasText(mrn) || hasEmtaEarlyMrnFromAck({
      country, security, mrn, hasSubmissionConfirmedMessage, hasMRNAllocatedMessage,
    })
  }

  if (status === 'IN_REVIEW') {
    return role !== 'ADMIN'
  }

  return true
}

export const isAmendButtonDisabled = ({
  status,
  mrn,
  additionalDeclarationType,
  country,
  security,
  role,
  hasSubmissionConfirmedMessage,
  hasMRNAllocatedMessage,
  isPreLodgedPresentationNotificationPending,
  isPreLodgedPresentationNotificationCorrectionPending,
}: ActionRuleContext): boolean => {
  if (status == null || ['SUBMITTED', 'DRAFT', 'DELETED', 'CANCEL_REQUESTED'].includes(status)) {
    return true
  }

  if (country === 'SE') return true

  if (isPreLodgedPresentationOutstanding({
    country, additionalDeclarationType, isPreLodgedPresentationNotificationPending,
  })) {
    return true
  }

  if (status === 'IN_REVIEW') {
    return role !== 'ADMIN'
  }

  if (['SUBMISSION_CONFIRMED'].includes(status) && (country === 'RO' || isPreLodgedDeclaration({ country, additionalDeclarationType }))) return false

  // The refusal drops the declaration back to REJECTED, where the ordinary rule
  // below would demand an MRN a pre-lodged declaration does not have yet and
  // leave the movement with no way forward at all — IE170 refused, IE170 out of
  // sequence, amend disabled. Amending is the way forward, so it stays open.
  if (isPreLodgedDeclaration({ country, additionalDeclarationType })
    && isPreLodgedPresentationNotificationCorrectionPending) {
    return false
  }

  if (status === 'DELETED' || status === 'TRANSIT_OPEN' || status === 'PROCEDURE_ENDED' || status === 'T1_TRANSIT_CLOSED' || status === 'CANCELED' || status === 'TRANSIT_OPEN' || status === 'TRANSIT_OPEN') {
    return true
  }

  if (status === 'REJECTED' && hasEmtaEarlyMrnFromAck({
    country, security, mrn, hasSubmissionConfirmedMessage, hasMRNAllocatedMessage,
  })) {
    return false
  }

  return !hasText(mrn)
}

export const isCancelButtonDisabled = ({
  status,
  mrn,
  country,
  role,
  additionalDeclarationType,
  isPreLodgedPresentationNotificationPending,
  isPreLodgedPresentationNotificationCorrectionPending,
}: ActionRuleContext): boolean => {
  if (status == null || ['SUBMITTED', 'DRAFT', 'DELETED', 'CANCEL_REQUESTED'].includes(status)) {
    return true
  }

  if (isPreLodgedPresentationOutstanding({
    country, additionalDeclarationType, isPreLodgedPresentationNotificationPending,
  })) {
    return true
  }

  if (status === 'IN_REVIEW') {
    return role !== 'ADMIN'
  }

  if (!['LT', 'HU', 'GB'].includes(country) && ['SUBMISSION_CONFIRMED', 'AMENDMENT_ACCEPTANCE'].includes(status)) return false

  if (status === 'CONTROL_DECISION'
    && isPreLodgedDeclaration({ country, additionalDeclarationType })
    && getPreLodgedDeclarationConfig(country)?.cancelAfterControlNotice) {
    return false
  }

  // Amending is not the only answer to a refused presentation: the dates that PL rejected may have
  // passed because the consignment is no longer moving at all, and withdrawing it is then the
  // correct outcome. The declaration is still registered — it accepts IE013 — so it can be
  // invalidated, and the refusal must not strand it in a state with no exit but amendment.
  //
  // Cancelling without an MRN is not new here: the carve-out above has enabled it throughout the
  // pre-lodged window since before pre-lodgement was modelled at all. Whether customs accepts an
  // IE014 in this particular state is unverified — the client cannot see the message it builds.
  if (isPreLodgedDeclaration({ country, additionalDeclarationType })
    && isPreLodgedPresentationNotificationCorrectionPending) {
    return false
  }

  if (status === 'DELETED' || status === 'TRANSIT_OPEN' || status === 'PROCEDURE_ENDED' || status === 'T1_TRANSIT_CLOSED' || status === 'CANCELED' || status === 'TRANSIT_OPEN' || status === 'TRANSIT_OPEN') {
    return true
  }

  return !hasText(mrn)
}

